import { useState, useRef, useEffect, useCallback } from "react";
import { message } from "antd";
import {
  BookingInfo,
  Product,
  ContainerInfo,
} from "../types/container-unloading";
import { hapticFeedback } from "../utils/containerUnloadingHelpers";
import { filterBookings } from "../utils/containerUnloadingHelpers";

interface ScannedProductModalData {
  product: Product | null;
  booking: BookingInfo | null;
  visible: boolean;
}

interface UseBarcodeScannerProps {
  bookings: BookingInfo[];
  container: ContainerInfo | null;
  loading?: boolean;
  setExpandedBookings: React.Dispatch<React.SetStateAction<Set<number>>>;
  updateProductCartons: (productId: number, cartons: number) => void;
  fetchContainerData: () => Promise<void>;
  setContainerResetModalVisible: (visible: boolean) => void;
  deliveryModalVisible: boolean;
  containerResetModalVisible: boolean;
  setShortcutsModalVisible: (visible: boolean) => void;
  setActiveTab: (tab: string) => void;
  searchQuery: string;
  expandedBookings: Set<number>;
}

export const useBarcodeScanner = ({
  bookings,
  container,
  loading = false,
  setExpandedBookings,
  updateProductCartons,
  fetchContainerData,
  setContainerResetModalVisible,
  deliveryModalVisible,
  containerResetModalVisible,
  setShortcutsModalVisible,
  setActiveTab,
  searchQuery,
  expandedBookings,
}: UseBarcodeScannerProps) => {
  const [highlightedProductId, setHighlightedProductId] = useState<
    number | null
  >(null);
  const [scannedProductModal, setScannedProductModal] =
    useState<ScannedProductModalData>({
      product: null,
      booking: null,
      visible: false,
    });
  const productRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [autoScanEnabled, setAutoScanEnabled] = useState(false);
  const barcodeBuffer = useRef("");
  const barcodeTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastScannedEAN = useRef<string>("");
  const lastScanTime = useRef<number>(0);
  const lastHighlightedProductId = useRef<number | null>(null); // Track which product was last highlighted

  // Use refs to always have latest values in event handlers
  const autoScanEnabledRef = useRef(autoScanEnabled);
  const isUnloadingInProgressRef = useRef(
    container?.unloading_status === "in_progress"
  );

  // Update refs when values change
  useEffect(() => {
    autoScanEnabledRef.current = autoScanEnabled;
  }, [autoScanEnabled]);

  useEffect(() => {
    isUnloadingInProgressRef.current =
      container?.unloading_status === "in_progress";
  }, [container?.unloading_status]);

  const isUnloadingInProgress = container?.unloading_status === "in_progress";

  // Search for product by EAN from barcode scanner or search input
  const searchProductByEAN = useCallback(
    async (scannedCode: string, fromScanner: boolean = false) => {
      console.log(
        `🚀 searchProductByEAN called with: code="${scannedCode}", fromScanner=${fromScanner}`
      );

      if (!scannedCode.trim()) {
        setHighlightedProductId(null);
        return;
      }

      // Don't search if data is still loading or no bookings available
      if (loading || bookings.length === 0) {
        console.log(
          `⏳ Skipping EAN search - loading=${loading}, bookings=${bookings.length}`
        );
        if (fromScanner) {
          message.warning("Container data still loading. Please wait...", 2);
        }
        return;
      }

      const currentTime = Date.now();
      const trimmedCode = scannedCode.trim();

      // Prevent duplicate scans within 100ms of the same EAN (only for scanner input)
      // This catches hardware duplicates but allows intentional re-scans to increment count
      if (
        fromScanner &&
        lastScannedEAN.current === trimmedCode &&
        currentTime - lastScanTime.current < 100
      ) {
        console.log("🚫 Duplicate scan prevented (hardware duplicate)");
        return;
      }

      // Update last scan tracking
      lastScannedEAN.current = trimmedCode;
      lastScanTime.current = currentTime;

      // Find product with matching EAN
      let foundProduct: Product | null = null;
      let foundBooking: BookingInfo | null = null;

      // Debug: Log available data
      console.log(
        `🔍 Searching for EAN: "${trimmedCode}" (length: ${trimmedCode.length})`
      );
      console.log(`📊 Available bookings: ${bookings.length}`);

      let totalProducts = 0;
      let productsWithEANs = 0;
      for (const booking of bookings) {
        totalProducts += booking.products.length;
        for (const product of booking.products) {
          if (product.ean) {
            productsWithEANs++;
            console.log(
              `   Product EAN: "${
                product.ean
              }" (type: ${typeof product.ean}) for "${product.name}"`
            );
          }
        }
      }
      console.log(
        `📦 Total products: ${totalProducts}, Products with EANs: ${productsWithEANs}`
      );

      // First try exact match
      for (const booking of bookings) {
        for (const product of booking.products) {
          if (product.ean && product.ean.toString() === trimmedCode) {
            foundProduct = product;
            foundBooking = booking;
            console.log(
              `✅ Exact match found: "${product.ean}" for "${product.name}" in booking ${booking.booking_reference}`
            );
            break;
          }
        }
        if (foundProduct) break;
      }

      // If no exact match, try flexible matching (handle leading zeros, etc.)
      if (!foundProduct) {
        console.log(`🔄 No exact match, trying flexible matching...`);
        for (const booking of bookings) {
          for (const product of booking.products) {
            if (product.ean) {
              const productEAN = product.ean.toString();
              const scannedEAN = trimmedCode;

              // Try various matching strategies
              if (
                productEAN === scannedEAN || // exact match
                productEAN.padStart(13, "0") === scannedEAN || // pad product EAN to 13 digits
                productEAN === scannedEAN.padStart(productEAN.length, "0") || // pad scanned EAN to match product EAN length
                productEAN.replace(/^0+/, "") === scannedEAN.replace(/^0+/, "") // remove leading zeros from both
              ) {
                foundProduct = product;
                foundBooking = booking;
                console.log(
                  `✅ Flexible match found: product EAN "${productEAN}" matches scanned "${scannedEAN}" for "${product.name}" in booking ${booking.booking_reference}`
                );
                break;
              }
            }
          }
          if (foundProduct) break;
        }
      }

      // If no exact match and code is 8 digits, try partial match (scanner truncation workaround)
      if (
        !foundProduct &&
        trimmedCode.length === 8 &&
        /^\d{8}$/.test(trimmedCode)
      ) {
        console.log(
          `🔍 No exact match for 8-digit code "${trimmedCode}", trying partial match...`
        );

        const partialMatches: { product: Product; booking: BookingInfo }[] = [];

        for (const booking of bookings) {
          for (const product of booking.products) {
            if (product.ean && product.ean.toString().startsWith(trimmedCode)) {
              partialMatches.push({ product, booking });
            }
          }
        }

        if (partialMatches.length === 1) {
          // Single match found - use it
          foundProduct = partialMatches[0].product;
          foundBooking = partialMatches[0].booking;
          console.log(
            `✅ Found single partial match: EAN ${foundProduct.ean} starts with ${trimmedCode}`
          );
        } else if (partialMatches.length > 1) {
          // Multiple matches - show warning but use first one
          foundProduct = partialMatches[0].product;
          foundBooking = partialMatches[0].booking;
          console.warn(
            `⚠️ Multiple partial matches found for "${trimmedCode}":`,
            partialMatches.map((m) => m.product.ean).join(", ")
          );
        } else {
          console.log(
            `❌ No partial matches found for 8-digit code "${trimmedCode}"`
          );
        }
      }

      if (foundProduct && foundBooking) {
        // Check if this is the same product as the last one BEFORE updating the ref
        // This determines if we should increment (same product) or just highlight (new product)
        const isSameProductAsLast =
          lastHighlightedProductId.current === foundProduct.id;

        // Auto-switch to the correct tab if product is in a different tab
        const correctTab = foundBooking.delivery_type; // "delivery" or "storage"
        setActiveTab(correctTab);

        // Auto-expand the booking to show the product
        setExpandedBookings((prev) => new Set([...prev, foundBooking.id]));

        // Briefly highlight the product with enhanced styling
        setHighlightedProductId(foundProduct.id);

        // Track which product was highlighted (for auto-increment logic)
        lastHighlightedProductId.current = foundProduct.id;

        // Add haptic feedback for mobile devices
        hapticFeedback();

        // Show product modal instead of scrolling - much cleaner UX
        console.log(
          `🎯 Showing scanned product modal for: ${foundProduct.name}`
        );
        setScannedProductModal({
          product: foundProduct,
          booking: foundBooking,
          visible: true,
        });

        // Clear highlight after modal appears (brief flash effect)
        setTimeout(() => {
          setHighlightedProductId(null);
        }, 500);

        // Determine if we should auto-increment
        // Only auto-increment if:
        // 1. It's from the scanner (not search input)
        // 2. Auto-scan is enabled
        // 3. Unloading is in progress
        // 4. Product is not completed (actual_cartons < estimated_cartons)
        // 5. This is the SAME product as the last highlighted one (not the first scan)
        const currentCartons = foundProduct.actual_cartons || 0;
        const isProductCompleted =
          currentCartons >= foundProduct.estimated_cartons;
        const shouldAutoIncrement =
          fromScanner &&
          autoScanEnabled &&
          isUnloadingInProgress &&
          !isProductCompleted &&
          isSameProductAsLast; // Only increment if it's the same product as last time

        if (shouldAutoIncrement) {
          const currentCartons = foundProduct.actual_cartons || 0;
          const newCartons = currentCartons + 1;
          console.log(
            `✅ Auto incrementing from ${currentCartons} to ${newCartons}`
          );

          try {
            await updateProductCartons(foundProduct.id, newCartons);

            // Product found successfully - no notification needed, modal will show
          } catch (error) {
            console.error("Error updating product cartons:", error);
            message.error("Failed to update carton count", 3);
          }
        } else {
          // Just highlight/open the product without incrementing
          console.log(
            `📍 Product highlighted: fromScanner=${fromScanner}, autoScanEnabled=${autoScanEnabled}, isUnloadingInProgress=${isUnloadingInProgress}, completed=${foundProduct.completed}`
          );

          // Product found successfully - no notification needed, modal will show
        }

        // Keep highlight until a new product is scanned (no auto-clear)
        // The highlight will only clear when:
        // 1. A different product is scanned (new highlight replaces old one)
        // 2. User manually clears search
      } else {
        console.error(`❌ No product found with EAN: "${scannedCode}"`);
        console.log(
          `🔍 Debug info: scannedCode="${scannedCode}", trimmedCode="${trimmedCode}", bookings.length=${bookings.length}`
        );
        message.warning(`No product found with EAN: ${scannedCode}`, 3);
        setHighlightedProductId(null);
      }
    },
    [
      bookings,
      loading,
      autoScanEnabled,
      isUnloadingInProgress,
      setActiveTab,
      setExpandedBookings,
      updateProductCartons,
    ]
  );

  // Global keypress listener for barcode scanning
  // Optimized for Zebra Android scanners which send rapid keyboard input
  useEffect(() => {
    let scannerBuffer = ""; // Separate buffer for scanner detection
    let scannerTimeout: NodeJS.Timeout | null = null;

    // Create a hidden input field to capture scanner data when no input is focused
    const hiddenScannerInput = document.createElement("input");
    hiddenScannerInput.type = "text";
    hiddenScannerInput.style.position = "absolute";
    hiddenScannerInput.style.left = "-9999px";
    hiddenScannerInput.style.top = "-9999px";
    hiddenScannerInput.style.width = "1px";
    hiddenScannerInput.style.height = "1px";
    hiddenScannerInput.style.opacity = "0";
    hiddenScannerInput.style.pointerEvents = "none";
    hiddenScannerInput.setAttribute("data-scanner-input", "true");
    hiddenScannerInput.setAttribute("aria-hidden", "true");
    // Prevent keyboard from opening on mobile devices
    hiddenScannerInput.setAttribute("inputmode", "none");
    hiddenScannerInput.setAttribute("autocomplete", "off");
    hiddenScannerInput.setAttribute("autocorrect", "off");
    hiddenScannerInput.setAttribute("autocapitalize", "off");
    hiddenScannerInput.setAttribute("spellcheck", "false");
    // Set styles individually to avoid type issues
    hiddenScannerInput.style.setProperty("webkitUserSelect", "none");
    hiddenScannerInput.style.setProperty("userSelect", "none");
    hiddenScannerInput.style.setProperty("webkitTouchCallout", "none");
    hiddenScannerInput.style.setProperty(
      "webkitTapHighlightColor",
      "transparent"
    );

    // Add the hidden input to the document
    document.body.appendChild(hiddenScannerInput);

    // Function to manage focus for scanner input
    const ensureScannerInputFocused = () => {
      const activeElement = document.activeElement;
      const inputs = document.querySelectorAll("input, textarea");

      // Check if any visible input is focused
      let visibleInputFocused = false;
      for (const input of inputs) {
        if (
          input === activeElement &&
          !input.hasAttribute("data-scanner-input")
        ) {
          visibleInputFocused = true;
          break;
        }
      }

      // If no visible input is focused, focus the hidden scanner input
      if (!visibleInputFocused && activeElement !== hiddenScannerInput) {
        console.log("🎯 Focusing hidden scanner input for barcode capture");
        // Use setTimeout to avoid triggering keyboard on some mobile devices
        setTimeout(() => {
          hiddenScannerInput.focus();
        }, 10);
      }
    };

    // Focus management
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Element;
      // If clicked outside any input, ensure scanner input is focused
      if (!target.closest("input, textarea")) {
        setTimeout(() => {
          ensureScannerInputFocused();
        }, 50);
      }
    };

    // Focus the hidden input initially (with delay to prevent keyboard)
    setTimeout(() => {
      hiddenScannerInput.focus();
    }, 500); // Longer delay to ensure page is fully loaded

    document.addEventListener("click", handleDocumentClick);

    const handleKeyPress = (event: KeyboardEvent) => {
      // Check if target is an input field
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      // Handle Enter key globally (for scanners that send Enter)
      if (event.key === "Enter") {
        console.log(`⏎ Enter key pressed globally`);
        // Let the input handler deal with Enter if it's in a field
        if (!isInputField && barcodeBuffer.current.length >= 8) {
          event.preventDefault();
          const scannedCode = barcodeBuffer.current.trim();
          console.log(
            `⏎ Processing barcode from global Enter: "${scannedCode}"`
          );
          processScannedCode(scannedCode);
        }
        return;
      }

      // For global numeric input (not in input fields), capture it
      if (!isInputField && /^\d$/.test(event.key)) {
        console.log(`📱 Capturing numeric input globally: "${event.key}"`);

        // Clear existing timeout
        if (barcodeTimeout.current) {
          clearTimeout(barcodeTimeout.current);
        }

        // Add character to buffer
        barcodeBuffer.current += event.key;
        console.log(
          `📝 Adding character "${event.key}" to global buffer. Current buffer: "${barcodeBuffer.current}" (length: ${barcodeBuffer.current.length})`
        );

        // Set timeout for processing
        barcodeTimeout.current = setTimeout(() => {
          const scannedCode = barcodeBuffer.current.trim();
          if (scannedCode.length >= 8) {
            console.log(
              `⏱️ Global scanner timeout reached, processing: "${scannedCode}" (length: ${scannedCode.length})`
            );
            processScannedCode(scannedCode);
          } else {
            barcodeBuffer.current = ""; // Clear short buffers
          }
        }, 150); // Very fast timeout for immediate processing
      }
    };

    const processScannedCode = (scannedCode: string) => {
      // Accept EAN codes of various lengths (8, 12, 13, 14 digits)
      if (scannedCode.length >= 8 && /^\d+$/.test(scannedCode)) {
        // Check if this looks like a truncated EAN
        if (scannedCode.length === 8) {
          console.warn(
            `⚠️ Detected 8-digit code: "${scannedCode}" - This might be truncated. Check scanner settings for full EAN transmission.`
          );
        }
        console.log(
          `✅ Valid EAN detected from Zebra scanner: ${scannedCode} (${scannedCode.length} digits)`
        );
        searchProductByEAN(scannedCode, true); // Mark as from scanner for auto-increment logic
      } else if (scannedCode.length >= 3) {
        // Log shorter codes for debugging
        console.log(
          `⚠️ Short scan detected: "${scannedCode}" - might be partial or non-EAN`
        );
      } else if (scannedCode.length > 0) {
        console.log(
          `❌ Very short scan: "${scannedCode}" - likely noise or partial transmission`
        );
      }
      barcodeBuffer.current = ""; // Clear buffer
    };

    // Handle paste events for manual EAN entry
    // When auto-scan is enabled, treat paste as scanner input (for testing/simulation)
    const handlePaste = (event: ClipboardEvent) => {
      const pastedData = event.clipboardData?.getData("text");
      if (pastedData && pastedData.length >= 8) {
        event.preventDefault();
        console.log(`📋 Pasted EAN code: ${pastedData}`);
        // If auto-scan is enabled, treat paste as scanner input (for testing)
        // Otherwise, just highlight/open without incrementing
        // Use refs to get latest values (not closure values)
        const treatAsScanner =
          autoScanEnabledRef.current && isUnloadingInProgressRef.current;
        console.log(
          `🔍🔍🔍 Paste handler DEBUG: autoScanEnabled=${autoScanEnabledRef.current}, isUnloadingInProgress=${isUnloadingInProgressRef.current}, treatAsScanner=${treatAsScanner}`
        );
        console.log(
          `🔍🔍🔍 Calling searchProductByEAN with fromScanner=${treatAsScanner}`
        );
        searchProductByEAN(pastedData, treatAsScanner);
      }
    };

    // Add event listeners for scanner detection
    document.addEventListener("keypress", handleKeyPress);
    document.addEventListener("keydown", handleKeyPress); // Fallback for some scanners
    document.addEventListener("paste", handlePaste);

    // Also listen for input events on all input/textarea elements (for Zebra scanners)
    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
      ) {
        const currentValue = target.value;
        const lastValue = target.getAttribute("data-last-value") || "";
        const newChars = currentValue.slice(lastValue.length);

        console.log(
          `🔤 Input event: last="${lastValue}", current="${currentValue}", newChars="${newChars}"`
        );

        // Check if this looks like scanner input (sudden numeric additions)
        if (currentValue.length > lastValue.length && /^\d+$/.test(newChars)) {
          // If it's numeric input, treat it as potential scanner data
          console.log(
            `📱 Detected numeric input: "${newChars}" (field: ${
              target.name || target.id || "unnamed"
            })`
          );

          // For short inputs (< 8 chars), accumulate in buffer
          if (newChars.length < 8) {
            scannerBuffer += newChars;
            console.log(`📝 Accumulated in buffer: "${scannerBuffer}"`);

            // Clear existing timeout
            if (scannerTimeout) {
              clearTimeout(scannerTimeout);
            }

            // Set timeout to process accumulated data
            scannerTimeout = setTimeout(() => {
              if (scannerBuffer.length >= 8) {
                console.log(
                  `⏱️ Processing accumulated buffer: "${scannerBuffer}"`
                );
                processScannerBuffer(target, currentValue);
              } else {
                console.log(
                  `⏳ Buffer too short, clearing: "${scannerBuffer}"`
                );
                scannerBuffer = "";
              }
            }, 300);
          } else {
            // Long numeric input - likely complete scanner data
            console.log(`🎯 Direct scanner input detected: "${newChars}"`);
            processDirectScannerInput(target, newChars, currentValue);
          }
        }

        target.setAttribute("data-last-value", currentValue);
      }
    };

    const processScannerBuffer = (
      target: HTMLInputElement | HTMLTextAreaElement,
      currentValue: string
    ) => {
      console.log(`🔄 Processing scanner buffer: "${scannerBuffer}"`);

      // Remove the scanned text from the input field
      const remainingText = currentValue.slice(0, -scannerBuffer.length);
      target.value = remainingText;
      target.setAttribute("data-last-value", remainingText);

      searchProductByEAN(scannerBuffer, true);
      scannerBuffer = "";
    };

    const processDirectScannerInput = (
      target: HTMLInputElement | HTMLTextAreaElement,
      scannedCode: string,
      currentValue: string
    ) => {
      console.log(`🎯 Processing direct scanner input: "${scannedCode}"`);

      // Remove the scanned text from the input field
      const remainingText = currentValue.slice(0, -scannedCode.length);
      target.value = remainingText;
      target.setAttribute("data-last-value", remainingText);

      searchProductByEAN(scannedCode, true);
    };

    // Hidden scanner input for capturing barcode data

    const handleFocus = (event: Event) => {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;

      // If a visible input is focused, blur the hidden scanner input
      if (target && !target.hasAttribute("data-scanner-input")) {
        console.log(
          `🎯 Visible input focused: ${target.name || target.id || "unnamed"}`
        );
        if (document.activeElement === hiddenScannerInput) {
          hiddenScannerInput.blur();
        }
      } else if (target?.hasAttribute("data-scanner-input")) {
        console.log(`🎯 Hidden scanner input focused`);
      }
    };

    const handleBlur = (event: Event) => {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      if (target && !target.hasAttribute("data-scanner-input")) {
        console.log(
          `🎯 Visible input blurred: ${target.name || target.id || "unnamed"}`
        );

        // When a visible input is blurred, refocus the hidden scanner input after a delay
        setTimeout(() => {
          ensureScannerInputFocused();
        }, 100);
      }
    };

    // Attach input listeners to existing and future input elements
    const attachInputListeners = () => {
      const inputs = document.querySelectorAll("input, textarea");
      inputs.forEach((input) => {
        // Skip if already has listeners (prevent duplicates)
        if (input.hasAttribute("data-scanner-listeners")) return;
        input.setAttribute("data-scanner-listeners", "true");

        input.addEventListener("input", handleInput);
        input.addEventListener("focus", handleFocus);
        input.addEventListener("blur", handleBlur);
        input.addEventListener("beforeinput", handleInput); // Additional event
        input.addEventListener("textInput", handleInput); // Fallback event

        // Initialize last value tracking
        const inputElement = input as HTMLInputElement | HTMLTextAreaElement;
        input.setAttribute("data-last-value", inputElement.value || "");
      });

      // Also attach to hidden scanner input
      if (!hiddenScannerInput.hasAttribute("data-scanner-listeners")) {
        hiddenScannerInput.setAttribute("data-scanner-listeners", "true");
        hiddenScannerInput.addEventListener("input", handleInput);
        hiddenScannerInput.addEventListener("focus", handleFocus);
        hiddenScannerInput.addEventListener("blur", handleBlur);
        hiddenScannerInput.addEventListener("beforeinput", handleInput);
        hiddenScannerInput.addEventListener("textInput", handleInput);
        hiddenScannerInput.setAttribute("data-last-value", "");
      }
    };

    // Initial attachment
    attachInputListeners();

    // Global scanner monitoring - check all inputs periodically for scanner data
    const scannerMonitorInterval = setInterval(() => {
      // Check hidden scanner input first
      const hiddenValue = hiddenScannerInput.value;
      const hiddenLastValue =
        hiddenScannerInput.getAttribute("data-last-value") || "";

      if (hiddenValue.length > hiddenLastValue.length) {
        const newChars = hiddenValue.slice(hiddenLastValue.length);
        console.log(`🔍 Hidden input has new data: "${newChars}"`);

        if (/^\d+$/.test(newChars) && newChars.length >= 8) {
          console.log(
            `🔍 Global monitor: processing hidden input data: "${newChars}"`
          );
          hiddenScannerInput.value = "";
          hiddenScannerInput.setAttribute("data-last-value", "");
          searchProductByEAN(newChars, true);
        }
      }
      hiddenScannerInput.setAttribute("data-last-value", hiddenValue);

      // Check other visible inputs
      const inputs = document.querySelectorAll(
        "input:not([data-scanner-input]), textarea"
      );
      inputs.forEach((input) => {
        const inputElement = input as HTMLInputElement | HTMLTextAreaElement;
        const currentValue = inputElement.value;
        const lastValue = input.getAttribute("data-last-value") || "";

        if (currentValue.length > lastValue.length) {
          const newChars = currentValue.slice(lastValue.length);

          // Look for long numeric sequences that could be scanner data
          if (/^\d+$/.test(newChars) && newChars.length >= 8) {
            console.log(
              `🔍 Global monitor detected scanner data: "${newChars}" in field ${
                inputElement.name || inputElement.id || "unnamed"
              }`
            );

            // Remove from input and process
            const remainingText = currentValue.slice(0, -newChars.length);
            inputElement.value = remainingText;
            input.setAttribute("data-last-value", remainingText);

            searchProductByEAN(newChars, true);
          } else if (
            /^\d+$/.test(newChars) &&
            newChars.length >= 3 &&
            newChars.length < 8
          ) {
            // Accumulate shorter sequences
            scannerBuffer += newChars;
            console.log(`🔍 Global monitor accumulated: "${scannerBuffer}"`);

            if (scannerTimeout) clearTimeout(scannerTimeout);
            scannerTimeout = setTimeout(() => {
              if (scannerBuffer.length >= 8) {
                console.log(
                  `🔍 Global monitor processing accumulated: "${scannerBuffer}"`
                );
                searchProductByEAN(scannerBuffer, true);
              }
              scannerBuffer = "";
            }, 500);
          }
        }

        // Update last value
        input.setAttribute("data-last-value", currentValue);
      });
    }, 100); // Check every 100ms

    // Re-attach when DOM changes (for dynamically created inputs)
    const observer = new MutationObserver(() => {
      attachInputListeners();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup
    return () => {
      document.removeEventListener("keypress", handleKeyPress);
      document.removeEventListener("keydown", handleKeyPress);
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("click", handleDocumentClick);
      clearInterval(scannerMonitorInterval);

      // Remove listeners from all inputs including hidden one
      document.querySelectorAll("input, textarea").forEach((input) => {
        input.removeEventListener("input", handleInput);
        input.removeEventListener("focus", handleFocus);
        input.removeEventListener("blur", handleBlur);
        input.removeEventListener("beforeinput", handleInput);
        input.removeEventListener("textInput", handleInput);
        input.removeAttribute("data-scanner-listeners");
        input.removeAttribute("data-last-value");
      });

      // Remove hidden input from DOM
      if (hiddenScannerInput.parentNode) {
        hiddenScannerInput.parentNode.removeChild(hiddenScannerInput);
      }

      observer.disconnect();
      if (barcodeTimeout.current) {
        clearTimeout(barcodeTimeout.current);
      }
      if (scannerTimeout) {
        clearTimeout(scannerTimeout);
      }
    };
  }, [autoScanEnabled, searchProductByEAN]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyPress = (event: KeyboardEvent) => {
      // Only handle if not in an input field or modal
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement ||
        deliveryModalVisible ||
        containerResetModalVisible
      ) {
        return;
      }

      // IMPORTANT: Don't interfere with barcode scanning
      // If we're currently accumulating characters in the buffer, ignore keyboard shortcuts
      if (barcodeBuffer.current.length > 0) {
        console.log(
          `🚫 Ignoring keyboard shortcut "${event.key}" - barcode scanning in progress`
        );
        return;
      }

      const key = event.key.toLowerCase();

      // Show shortcuts modal (Cmd/Ctrl + ?)
      if (key === "?" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setShortcutsModalVisible(true);
        return;
      }

      // Tab navigation (1-2)
      if (!event.ctrlKey && !event.altKey && !event.metaKey) {
        if (key === "1") {
          event.preventDefault();
          setActiveTab("shipping");
          return;
        }
        if (key === "2") {
          event.preventDefault();
          setActiveTab("storage");
          return;
        }

        // Toggle auto-scan mode
        if (key === "s") {
          event.preventDefault();
          setAutoScanEnabled((prev) => !prev);
          message.info(
            `Zebra Scanner Mode ${!autoScanEnabled ? "enabled" : "disabled"}`
          );
          return;
        }

        // Search products
        if (key === "f") {
          event.preventDefault();
          const searchInput = document.querySelector(
            'input[placeholder*="Search"]'
          ) as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
          return;
        }

        // Reset container (R)
        if (key === "r") {
          event.preventDefault();
          setContainerResetModalVisible(true);
          return;
        }

        // Expand/Collapse all bookings (E)
        if (key === "e") {
          event.preventDefault();
          const filteredBookings = filterBookings(
            bookings,
            searchQuery,
            setExpandedBookings
          );
          if (expandedBookings.size === filteredBookings.length) {
            // All expanded, collapse all
            setExpandedBookings(new Set());
            message.info("Collapsed all bookings");
          } else {
            // Expand all
            setExpandedBookings(new Set(filteredBookings.map((b) => b.id)));
            message.info("Expanded all bookings");
          }
          return;
        }
      }

      // Refresh data (Ctrl+R or Cmd+R)
      if (key === "r" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        fetchContainerData();
        message.info("Refreshing container data...");
        return;
      }
    };

    document.addEventListener("keydown", handleGlobalKeyPress);
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyPress);
    };
  }, [
    autoScanEnabled,
    deliveryModalVisible,
    containerResetModalVisible,
    fetchContainerData,
    searchQuery,
    expandedBookings,
    setActiveTab,
    setShortcutsModalVisible,
    bookings,
    setContainerResetModalVisible,
    setExpandedBookings,
  ]);

  return {
    highlightedProductId,
    productRefs,
    autoScanEnabled,
    setAutoScanEnabled,
    searchProductByEAN,
    scannedProductModal,
    setScannedProductModal,
  };
};
